// Contracts for «Golden Flower» (zhajinhua, 炸金花): a single-player three-card game against the house on the user's seed pair.
//   GET  /api/zhajinhua/state                       → ZjhStateResponse
//   POST /api/zhajinhua/start  body ZjhStartInput    → ZjhActionResponse
//   POST /api/zhajinhua/decide body ZjhDecideInput   → ZjhActionResponse
// Cards are ids 0…51: rank = id >> 2 (0 = '2' … 12 = 'A'), suit = id & 3 (0 ♠, 1 ♥, 2 ♣, 3 ♦).
import { z } from 'zod';
import { zAmount, zIdempotencyKey } from '../common';
import type { FairProof } from '../fair';

export const ZJH_CARDS = 52;
/** 炸金花 categories, low → high (same order as `ZJH_RANKING` in @tg/games/zhajinhua) */
export const ZJH_CATEGORIES = ['high', 'pair', 'straight', 'flush', 'straightFlush', 'trips'] as const;
export type ZjhCategory = (typeof ZJH_CATEGORIES)[number];
export const ZJH_ACTIONS = ['play', 'fold'] as const;
export type ZjhAction = (typeof ZJH_ACTIONS)[number];
export const ZJH_RESULTS = ['fold', 'noQualify', 'win', 'tie', 'lose'] as const;
export type ZjhResult = (typeof ZJH_RESULTS)[number];

export const zZjhStartInput = z
  .object({
    ante: zAmount,
    /** Pair Plus side bet, 0 = none */
    pairPlus: z.number().int().min(0).max(1_000_000_000),
    idempotencyKey: zIdempotencyKey,
  })
  .strict();
export const zZjhDecideInput = z.object({ gameId: z.string().min(1).max(64), action: z.enum(ZJH_ACTIONS) }).strict();
export type ZjhStartInput = z.infer<typeof zZjhStartInput>;
export type ZjhDecideInput = z.infer<typeof zZjhDecideInput>;

export type ZjhStatus = 'active' | 'folded' | 'played';

/** Returns of a finished game, stakes included (0 = that part lost). */
export interface ZjhParts {
  ante: number;
  play: number;
  anteBonus: number;
  pairPlus: number;
}

export interface ZjhGameView {
  gameId: string;
  betId: string;
  ante: number;
  pairPlus: number;
  /** Play stake: 0 until played, then = ante */
  play: number;
  status: ZjhStatus;
  /** the player's cards, dealt order */
  player: number[];
  playerHand: ZjhCategory;
  /** the dealer's cards and category — only once the game is over */
  dealer: number[] | null;
  dealerHand: ZjhCategory | null;
  /** did the dealer qualify (Q-high or better); null while active and after a fold */
  qualified: boolean | null;
  result: ZjhResult | null;
  parts: ZjhParts | null;
  /** total paid (0 while active) */
  payout: number;
}

export interface ZjhStateResponse {
  game: ZjhGameView | null;
  fair: { serverSeedHash: string; clientSeed: string; nonce: number };
  minBet: number;
  maxBet: number;
  maxWin: number;
}

export interface ZjhActionResponse {
  game: ZjhGameView;
  balance: number;
  fair: FairProof;
  duplicate: boolean;
}

/** BetDetail.zhajinhua: enough to re-deal and check the game (dealer only once it is over). */
export interface ZjhBetContext {
  ante: number;
  pairPlus: number;
  action: ZjhAction | null;
  player: number[];
  dealer: number[] | null;
}
